import { useCallback, useMemo, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { CustomTagProps } from "rc-select/lib/BaseSelect";

import { SignalPreviewCountsByKey } from "lib/hooks/api/signals/useSignalPreviewCounts";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useDebouncedValue } from "../../../../../lib/debounce";
import { useOrganisations } from "../../../../../lib/hooks/api/organisations/useOrganisations";
import { OrgSortBy, OrgSortOrder, SearchOrgRole } from "../../../../../lib/types/graphQLEnums";
import InfiniteSuppliersChecklist from "../../../../form_components/dropdown_list_selects/new_supplier_selects/InfiniteSuppliersChecklist";
import { SupplierOption } from "../../../../form_components/dropdown_list_selects/new_supplier_selects/types";
import { Select, SelectProps } from "../../../../form_components/Inputs";
import SkeletonText from "../../../../ui/skeleton/SkeletonText";
import Tag, { TagVariant } from "../../../../ui/tag/Tag";
import Tooltip from "../../../../ui/tooltip/Tooltip";

type SimpleSupplierSelectProps<T extends FieldValues> = Omit<SelectProps<T>, "options"> & {
  tagVariant?: TagVariant;
  counts?: SignalPreviewCountsByKey;
  isLoadingCounts?: boolean;
  maxTagWidth?: number;
};

const dedupeSuppliers = (suppliers: SupplierOption[]): SupplierOption[] => {
  return Array.from(new Map(suppliers.map((item) => [item.value, item])).values());
};

export default function SimpleSupplierSelect<T extends FieldValues>({
  tagVariant,
  counts,
  isLoadingCounts = false,
  maxTagWidth,
  ...props
}: SimpleSupplierSelectProps<T>) {
  const { field } = useController(props);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchInput);

  const {
    data: initialSuppliers,
    isInitialLoading,
    isError,
  } = useOrganisations(
    {
      ids: field.value,
      sortBy: OrgSortBy.AwardCount,
      sortOrder: OrgSortOrder.Desc,
      role: SearchOrgRole.Supplier,
      limit: field.value.length,
      page: 1,
      include: { awardCount: true, noticeCount: false, aliases: true },
    },
    { enabled: field.value.length > 0, keepPreviousData: true },
  );

  const supplierOptions = useMemo(() => {
    const suppliers =
      initialSuppliers?.searchOrganisations.orgs.map((org) => ({
        label: org.name,
        value: org.id,
      })) ?? [];
    return dedupeSuppliers(suppliers);
  }, [initialSuppliers]);

  const handleSupplierChange = (suppliers: SupplierOption[]) => {
    const uniqueSuppliers = dedupeSuppliers(suppliers);
    field.onChange(uniqueSuppliers.map((supplier) => supplier.value));
  };

  const TagRender = useCallback(
    ({ value }: CustomTagProps) => {
      const supplier = supplierOptions.find((s) => s.value === value);
      // Show the count whenever we have one — including an explicit (0). Only a
      // genuinely unknown count (undefined) falls back to the bare label.
      const label =
        counts?.[value] !== undefined
          ? `${supplier?.label} (${counts[value]})`
          : supplier?.label || value;

      const handleRemove = () => {
        const updatedValues = field.value.filter((v: string) => v !== value);
        field.onChange(updatedValues);
      };

      // Spin while the org details load, or while its match count is still unknown.
      const isLoading = isInitialLoading || (isLoadingCounts && counts?.[value] === undefined);

      const count = counts?.[value];
      let tooltipContent: string | undefined;
      if (count === 0) {
        tooltipContent = "No matches in the last 6 months";
      } else if (count !== undefined) {
        tooltipContent = `Matched ${count.toLocaleString()} notice${count === 1 ? "" : "s"} in the last 6 months`;
      }

      // Tag isn't a ref-forwarding component, so wrap it for the tooltip trigger.
      return (
        <Tooltip content={tooltipContent}>
          <TagTooltipTrigger>
            <Tag
              maxWidth={maxTagWidth}
              label={label}
              icon={<UIcon icon="xClose" size={12} />}
              variant={tagVariant}
              onClick={handleRemove}
              isLoading={isLoading}
            />
          </TagTooltipTrigger>
        </Tooltip>
      );
    },
    [counts, field, isInitialLoading, isLoadingCounts, maxTagWidth, supplierOptions, tagVariant],
  );

  if (isInitialLoading) return <SkeletonText width="100%" />;
  if (isError) return <div>Error loading suppliers</div>;

  return (
    <ClassNames>
      {({ css }) => (
        <Select
          {...props}
          // Targets the shared antd Select internals; className is generated by
          // Emotion (ClassNames render prop) and forwarded onto the antd select root.
          className={css({
            width: "100%",
            "& > div > div": {
              display: "flex",
              gap: 8,
              padding: 8,
              "& span": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& :focus-within": {
                margin: 0,
              },
            },
          })}
          selectAll={!!debouncedSearch}
          options={supplierOptions}
          value={field.value}
          dropdownRender={() => (
            <InfiniteSuppliersChecklist
              signalSuppliers={[]}
              selectedSuppliers={supplierOptions.filter((s) => field.value.includes(s.value))}
              excludeSignals
              textSearch={debouncedSearch}
              onChange={handleSupplierChange}
            />
          )}
          onSearch={setSearchInput}
          tagRender={TagRender}
          dropdownStyle={debouncedSearch === "" ? { display: "none" } : undefined}
          onChange={(value) => {
            const values = Array.isArray(value) ? value : [value];
            const validSuppliers = values.filter(
              (v): v is SupplierOption =>
                typeof v === "object" && v !== null && "value" in v && "label" in v,
            );
            field.onChange(validSuppliers.map((s) => s.value));
          }}
          getPopupContainer={() => document.body}
        />
      )}
    </ClassNames>
  );
}

// Wrapper that acts as the tooltip trigger around each Tag. Opt out of the
// ellipsis clipping applied to spans inside the select so the Tag renders fully.
const TagTooltipTrigger = styled.span({
  display: "inline-flex",
  overflow: "visible",
});
